/**
 * SPEC-08 §3.3 — `<PageHeader/>`.
 *
 * Replaces the big `<Card><CardHeader>` header block in
 * `EngagementResultsPage` with a slim sticky chrome row:
 *
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │ <breadcrumb> ›  Client name · period · currency · calculated  │
 *   │                                                       [actions]│
 *   └───────────────────────────────────────────────────────────────┘
 *
 * `sticky top-0 z-20 bg-background/95 backdrop-blur border-b`, ~56px
 * tall. NOT a raised card; it's app chrome.
 *
 * Test-ids from the previous header MUST carry over — callers pass them
 * directly onto the action elements / status badge. This component itself
 * doesn't own any of those test-ids.
 */

import { cn } from "@/lib/utils";

export interface PageHeaderProps {
  /** Inline node: breadcrumb / client name / page title. */
  primary: React.ReactNode;
  /** Inline node: period / currency / computed-at — typically `text-label`. */
  meta?: React.ReactNode;
  /** Right-side actions slot (status badge + buttons). */
  actions?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function PageHeader({
  primary,
  meta,
  actions,
  className,
  "data-testid": testId,
}: PageHeaderProps) {
  return (
    <div
      data-testid={testId ?? "page-header"}
      className={cn(
        "sticky top-0 z-20 -mx-6 mb-4 border-b bg-background/95 backdrop-blur",
        "px-6 py-2",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex flex-col">
          <div className="text-page-title text-foreground truncate">
            {primary}
          </div>
          {meta && (
            <div className="text-label flex flex-wrap items-center gap-x-2">
              {meta}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap">{actions}</div>
        )}
      </div>
    </div>
  );
}
